/**
 * Battery-optimization exemption bridge + prompt policy. OEM skins kill
 * services more aggressively than vanilla Android; the exemption helps.
 * Overlay shown each login until granted; "Not now" is session-scoped.
 */

import { ref, type Ref } from "vue";

import { registerPlugin } from "@capacitor/core";
import type { Plugin } from "@capacitor/core";

import { isAndroid } from "./platform";

interface BatteryOptimizationPluginApi extends Plugin {
  isIgnoringBatteryOptimizations(): Promise<{ ignoring: boolean }>;
  requestIgnoreBatteryOptimizations(): Promise<{ launched: boolean }>;
  openOemSettings(opts: { component?: string; action?: string }): Promise<{ opened: boolean }>;
}

const BatteryOptimization = registerPlugin<BatteryOptimizationPluginApi>("BatteryOptimization");

export default BatteryOptimization;

export type BatteryOptPromptState = "unasked" | "prompted" | "declined" | "granted";

// _v2: previous key got stuck in "declined" via an old resume heuristic.
const PROMPT_STATE_KEY = "knocknoc_battery_opt_prompt_state_v2";

// One-shot flag bridging the post-login full-page reload. postLoginNavigate
// uses window.location.assign (it must hit the server SPA shell for referrer
// redirects), which wipes in-memory state including the overlay ref. maybeShow
// sets this when it surfaces the overlay on login; the next bootstrap restores
// the overlay and clears it. Cleared once the prompt is otherwise resolved.
const PROMPT_SHOW_PENDING_KEY = "knocknoc_battery_opt_show_pending";

// Drives the overlay (mounted once in App.vue). Session-scoped, not persisted.
export const batteryOptimizationPromptVisible: Ref<boolean> = ref(false);

// True only between firing the system exemption intent and the next resume.
// Scopes the "prompted -> declined" demotion to an actual return from the
// system dialog, so unrelated resumes (the login foreground transition, the
// SAML browser closing, OTA, app switching) can't tear down a freshly shown
// login overlay.
let _awaitingSystemDialogResult = false;

async function readPromptState(): Promise<BatteryOptPromptState> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: PROMPT_STATE_KEY });
    if (value === "prompted" || value === "declined" || value === "granted") return value;
    return "unasked";
  } catch {
    return "unasked";
  }
}

async function writePromptState(value: BatteryOptPromptState): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: PROMPT_STATE_KEY, value });
  } catch {
    // Next launch re-evaluates from native state.
  }
}

async function writeShowPending(pending: boolean): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    if (pending) {
      await Preferences.set({ key: PROMPT_SHOW_PENDING_KEY, value: "1" });
    } else {
      await Preferences.remove({ key: PROMPT_SHOW_PENDING_KEY });
    }
  } catch {
    // Best-effort; a lost flag just means the overlay is not re-shown after the reload.
  }
}

export async function isBatteryOptimizationExempt(): Promise<boolean> {
  if (!isAndroid()) return true;
  try {
    const { ignoring } = await BatteryOptimization.isIgnoringBatteryOptimizations();
    return ignoring;
  } catch {
    return false;
  }
}

// Show overlay on login if not exempt. Ignores persisted "declined" by design.
export async function maybeShowBatteryOptimizationPrompt(): Promise<void> {
  if (!isAndroid()) return;
  if (await isBatteryOptimizationExempt()) {
    await writePromptState("granted");
    await writeShowPending(false);
    batteryOptimizationPromptVisible.value = false;
    return;
  }
  // Persist the intent so the overlay survives the post-login reload.
  await writeShowPending(true);
  batteryOptimizationPromptVisible.value = true;
}

// Re-surface the overlay after the post-login full-page reload wiped the
// in-memory ref. One-shot: clears the pending flag so an unrelated later reload
// does not resurface it. Caller gates on an active session.
export async function restorePendingBatteryOptimizationPrompt(): Promise<void> {
  if (!isAndroid()) return;
  let pending = false;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: PROMPT_SHOW_PENDING_KEY });
    pending = value === "1";
  } catch {
    return;
  }
  if (!pending) return;
  await writeShowPending(false);
  // The exemption may have been granted between login and this reload.
  if (await isBatteryOptimizationExempt()) {
    await writePromptState("granted");
    return;
  }
  batteryOptimizationPromptVisible.value = true;
}

// Fires ACTION_REQUEST_IGNORE_BATTERY_OPTIMIZATIONS; resume watcher reconciles.
export async function acceptBatteryOptimizationPrompt(): Promise<void> {
  if (!isAndroid()) {
    batteryOptimizationPromptVisible.value = false;
    return;
  }
  // User engaged with the overlay; the post-login restore bridge is done.
  await writeShowPending(false);
  if (await isBatteryOptimizationExempt()) {
    await writePromptState("granted");
    batteryOptimizationPromptVisible.value = false;
    return;
  }
  // Mark before firing so recheckAfterResume can detect a dismissal. The
  // in-memory flag tells recheck this resume is the return from the system
  // dialog (and not some unrelated foreground event).
  await writePromptState("prompted");
  _awaitingSystemDialogResult = true;
  try {
    await BatteryOptimization.requestIgnoreBatteryOptimizations();
  } catch {
    // Some MIUI builds block the intent.
    _awaitingSystemDialogResult = false;
    await writePromptState("declined");
    batteryOptimizationPromptVisible.value = false;
  }
}

export function dismissBatteryOptimizationPrompt(): void {
  batteryOptimizationPromptVisible.value = false;
  void writeShowPending(false);
}

// Run on resume; reconciles preference with native state.
export async function recheckBatteryOptimizationAfterResume(): Promise<void> {
  if (!isAndroid()) return;
  const exempt = await isBatteryOptimizationExempt();
  const state = await readPromptState();
  if (exempt) {
    _awaitingSystemDialogResult = false;
    if (state !== "granted") await writePromptState("granted");
    await writeShowPending(false);
    batteryOptimizationPromptVisible.value = false;
    return;
  }
  if (state === "prompted" && _awaitingSystemDialogResult) {
    // Returned from the system dialog without granting → user declined.
    _awaitingSystemDialogResult = false;
    await writePromptState("declined");
    await writeShowPending(false);
    batteryOptimizationPromptVisible.value = false;
  } else if (state === "granted") {
    // Revoked in system settings; allow a future login to re-ask.
    await writePromptState("unasked");
  }
  // Leave the rest alone: a resume that is not the return from the system
  // dialog (login foreground, browser close, OTA, app switch) must not advance
  // state or hide a freshly shown login overlay.
}

export async function getBatteryOptPromptState(): Promise<BatteryOptPromptState> {
  if (!isAndroid()) return "granted";
  return readPromptState();
}

// OEM settings launcher. See oem-remediation.ts for the (component, action) table.
export async function openOemSettings(opts: { component?: string; action?: string }): Promise<boolean> {
  if (!isAndroid()) return false;
  try {
    const { opened } = await BatteryOptimization.openOemSettings(opts);
    return opened;
  } catch {
    return false;
  }
}

let _watcherInstalled = false;

// Idempotent; one resume-listener per app lifetime.
export async function initBatteryOptimizationWatcher(): Promise<void> {
  if (!isAndroid() || _watcherInstalled) return;
  _watcherInstalled = true;

  const { App } = await import("@capacitor/app");
  await App.addListener("appStateChange", (state) => {
    if (state.isActive) {
      recheckBatteryOptimizationAfterResume();
    }
  });
}
